﻿import { Link } from 'react-router';
import { ArrowUpRight, Box, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUI } from '@/state/store';
import { useOverview } from './store';
import { FLOW_SOURCES, FLOW_STEP_COUNT, processingPartFor } from './flowReference';
import { STAGE_STORY } from './story';

/** Every asset row opens its model directly. Reference detail stays optional. */
export function StoryPanel() {
  const stageId = useOverview((s) => s.selectedStageId);
  const step = STAGE_STORY[stageId];
  const selectedAsset = useUI((s) => s.selection.assetId);
  return (
    <div className="flex flex-col gap-4 p-4 text-sm" data-testid="flow-inspector">
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-accent">
            Step {String(step.id).padStart(2, '0')} / {FLOW_STEP_COUNT}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              aria-label="Previous data flow step"
              disabled={stageId === 1}
              onClick={() => useOverview.getState().selectStage(stageId - 1)}
              className="rounded p-1 text-ink-2 hover:bg-overlay disabled:opacity-25"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next data flow step"
              disabled={stageId === FLOW_STEP_COUNT}
              onClick={() => useOverview.getState().selectStage(stageId + 1)}
              className="rounded p-1 text-ink-2 hover:bg-overlay disabled:opacity-25"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <h2 className="font-display text-lg font-semibold leading-tight text-ink">{step.name}</h2>
        <p className="mt-2 text-xs leading-relaxed text-ink-2">{step.detail}</p>
        {step.note && (
          <p className="mt-2 border-l-2 border-accent/50 pl-2 text-xs leading-relaxed text-ink-2">
            {step.note}
          </p>
        )}
      </div>
      <div className="flex gap-2 border-y border-hairline py-2 text-xs">
        <span className="shrink-0 text-ink-3">Output</span>
        <span className="text-ink">{step.output}</span>
      </div>
      <section aria-label="3D models for this step">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ink-3">
          Open a 3D model
        </div>
        <div className="divide-y divide-hairline/60">
          {step.assets.map((asset, index) => {
            const part = asset.id === 'ground_station' ? processingPartFor(step.id) : null;
            const params = new URLSearchParams();
            if (asset.subview) params.set('view', asset.subview);
            if (part) params.set('part', part);
            const query = params.toString();
            return (
              <Link
                key={`${asset.id}-${index}`}
                to={`/twin/${asset.id}${query ? `?${query}` : ''}`}
                onClick={() => useUI.getState().select({ assetId: asset.id, subsystemId: null })}
                aria-label={`Open ${asset.label} 3D model`}
                className={`group flex cursor-pointer items-center gap-2.5 rounded-control px-2 py-2.5 transition-colors hover:bg-accent-soft focus-visible:bg-accent-soft ${selectedAsset === asset.id ? 'bg-overlay/40' : ''}`}
              >
                <Box className="h-4 w-4 shrink-0 text-accent/80" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-ink">{asset.label}</span>
                  <span className="mt-0.5 block text-[10px] leading-4 text-ink-3">{asset.role}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-[10px] text-ink-3 group-hover:text-accent">
                  3D
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
      <details key={stageId} className="border-t border-hairline pt-3" data-testid="flow-reference">
        <summary className="cursor-pointer text-xs font-medium text-ink-2">
          How this step works & sources
        </summary>
        <ol className="mt-3 list-decimal space-y-2 pl-4 text-xs leading-relaxed text-ink-2">
          {step.process.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ol>
        <div className="mt-3 flex flex-col gap-2 border-t border-hairline pt-3">
          {step.sources.map((id) => (
            <a
              key={id}
              href={FLOW_SOURCES[id].url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] leading-4 text-accent hover:underline"
            >
              {FLOW_SOURCES[id].label} ↗
            </a>
          ))}
        </div>
        <p className="mt-3 text-[10px] leading-4 text-ink-3">
          Illustrative campus and data movement. These animations do not measure coverage, link availability
          or throughput. Mission contacts and telemetry sources are shown in Loader and Maintenance. Condition
          dots reflect the inspected twin, including simulated readings.
        </p>
      </details>
    </div>
  );
}
